import { memo, useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import _isEmpty from 'lodash/isEmpty';
//Local files
import { CardItem } from './EsignFlows.styled';
import EsignTemplatesGrid from 'components/EsignTemplates/Grid/Grid';
import EsignAnalysesList from 'components/Analyses/EsignAnalyses/List/List';
import ErrorWrapper from 'components/Common/ErrorWrapper/ErrorWrapper';
import useApp from 'hooks/useApp';
import useCustomSelector from 'hooks/useCustomSelector';
import useSuccess from 'hooks/useSuccess';
import useEsignFlows from 'hooks/useEsignFlows';
import useEsignAnalyses from 'hooks/useEsignAnalyses';
import useEsignTemplates from 'hooks/useEsignTemplates';
import { DELETE_ESIGN_FLOW } from 'helpers/confirmations';
import { checkEmptyString, checkValidEmail, needToUpdateField } from 'helpers';
import { esignFlowsSelectors } from "features/esignFlows";
import { esignTemplatesSelectors } from 'features/esignTemplates';

const EsignFlows = () => {
  const { openDeleteDialog, setEsignFlowDrawer } = useApp();
  const { setSuccess } = useSuccess();
  const { getEsignFlow, createEsignFlow, updateEsignFlow } = useEsignFlows();
  const { getEsignAnalyses, clearLocalEsignAnalyses } = useEsignAnalyses();
  const { getEsignTemplates, clearLocalEsignTemplates } = useEsignTemplates();
  const id = useCustomSelector(esignFlowsSelectors.getEsignFlowId);
  const esignFlow = useCustomSelector(esignFlowsSelectors.getEsignFlowData);
  const flowFetching = useCustomSelector(esignFlowsSelectors.esignFlowIsFetching);
  const esignTemplatesFetching = useCustomSelector(esignTemplatesSelectors.esignTemplatesIsFetching);
  const { pagination } = useCustomSelector(esignTemplatesSelectors.getEsignTemplatesData);
  const [email, setEmail] = useState({ value: esignFlow?.email, error: '' });
  const [context, setContext] = useState({ value: esignFlow?.context, error: '' });
  const [edit, setEdit] = useState(false);
  const [firstTimeOpened, setFirstTimeOpened] = useState(true);

  const handleEntering = (esign_flow) => {
    setEmail({ value: esign_flow?.email, error: '' });
    setContext({ value: esign_flow?.context, error: '' });
  };
  const handleExited = () => {
    setEmail({ value: '', error: '' });
    setContext({ value: '', error: '' });
    setEdit(false);
  };
  const handleSave = () => {
    let hasError = false;
    if (checkEmptyString(email.value)) {
      if (!checkValidEmail(email.value)) {
        setEmail(prev => ({ ...prev, error: `Email is invalid` }) );
        hasError = true;
      }
    }
    if (hasError) return;

    const esign_flow = {
      ...(needToUpdateField(esignFlow?.email, email.value) && {  email: email.value }),
      ...(needToUpdateField(esignFlow?.context, context.value) && {  context: context.value }),
    };

    if (!_isEmpty(esign_flow)) {
      if (!!id) {
        updateEsignFlow(esign_flow)
        .then(({ error }) => {
          if (!error) {
            setSuccess('Flow successfully updated');
          }
        })
        .finally(() => setEdit(false))
      } else {
        createEsignFlow(esign_flow)
        .then(({ error }) => {
          if (!error) {
            setSuccess('Flow successfully created');
          }
        })
      }
    } 
  }
  const fetchEsignTemplates = useCallback((offset) => {
    getEsignTemplates({ offset })
  }, [getEsignTemplates]);
  const handleScroll = ({ target: { scrollTop, scrollHeight, offsetHeight } }) => 
  scrollTop >= scrollHeight - offsetHeight && pagination.total_count > pagination.count + pagination.offset && !esignTemplatesFetching && fetchEsignTemplates(pagination.limit + pagination.offset);
  const handleDelete = () => openDeleteDialog({ id: '', type: DELETE_ESIGN_FLOW });
  const fetchEsignAnalyses = useCallback(() => {
    getEsignAnalyses({ offset: 0 })
  }, [getEsignAnalyses]);

  useEffect(() => {
    if (!id) {
      handleExited();
      clearLocalEsignAnalyses();
      clearLocalEsignTemplates();
    };
  }, [id, clearLocalEsignAnalyses, clearLocalEsignTemplates]);
  useEffect(() => {
    if (firstTimeOpened && !id) {
      getEsignFlow()
      .then(({ payload, error }) => {
        if (!error) {
          const { esign_flow } = payload;
          handleEntering(esign_flow);
          fetchEsignTemplates(0);
          fetchEsignAnalyses();
        }
      })
      .finally(() => setFirstTimeOpened(false));
    }

  }, [getEsignFlow, fetchEsignTemplates, fetchEsignAnalyses, firstTimeOpened, id]);

  return (
    <Box sx={{ flexGrow: 1, overflow: 'auto', p: 5 }}>
      <Container maxWidth={'sm'}>
        <Card sx={{ p: 2 }}>
          <CardItem>
            <Typography sx={{ fontSize: '1.75rem', fontWeight: 600 }}>Configuration</Typography>
            <Box>
              {!id && <Button variant='outlined' disabled={flowFetching} onClick={handleSave}>Create</Button>}
              {id && !edit && <Button variant='outlined' disabled={flowFetching} onClick={() => setEdit(true)}>Edit</Button>}
              {id && edit && <Button variant='outlined' disabled={flowFetching} sx={{ mr: '5px' }} onClick={handleSave}>Update</Button>}
              {id && edit && <Button variant='outlined' sx={{ color: '#E30000', borderColor: '#E30000' }} disabled={flowFetching} onClick={handleDelete}>Delete</Button>}
            </Box>
          </CardItem>
          <CardItem sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
            <TextField
              fullWidth
              label='Email'
              disabled={(!!id && !edit)}
              value={email.value}
              onChange={({ target: { value } }) => setEmail({ value, error: '' })}
              helperText={email.error}
              error={!!email.error}
            />
            <TextField
              fullWidth
              label='Context'
              multiline
              minRows={3}
              maxRows={10}
              disabled={(!!id && !edit)}
              helperText={context.error}
              error={!!context.error}
              value={context.value}
              onChange={({ target: { value } }) => setContext({ value, error: '' })}
            />
          </CardItem>
          <CardItem>
            <Typography sx={{ fontSize: '1.75rem', fontWeight: 600 }}>Templates</Typography>
            <Button variant='outlined' disabled={flowFetching} onClick={() => setEsignFlowDrawer({ open: true })}>Add</Button>
          </CardItem>
            <Container maxWidth={false} sx={{ overflow: 'auto', maxHeight: '150px' }} onScroll={handleScroll}>
              <EsignTemplatesGrid {...{fetching: esignTemplatesFetching, onFetchTemplates: fetchEsignTemplates }} />
            </Container>
          <CardItem>
          <Box display='flex' justifyContent='center' alignItems='center'> 
          </Box>
          </CardItem>
        </Card>
        <Card sx={{ p: 2, mt: 3 }}>
          <Typography sx={{ fontSize: '1.75rem', fontWeight: 600 }}>History</Typography>
          <EsignAnalysesList />
        </Card>    
      </Container>
    </Box>
  );
};

const WrappedComponent = props => {
  return (
    <ErrorWrapper>
      <EsignFlows {...props} />
    </ErrorWrapper>
  );
};

export default memo(WrappedComponent);